// @flow

import type { FetchOpts } from './fetch'

import fetch from './fetch'
import querystring from 'qs'
import KnownError from 'frame/core/KnownError'
import fengPerf from 'feng-performance'

type Query = { [string]: string };
type Headers = { [string]: string };
type prefOptoin = FetchOpts & {
  traceId: string,
  end: () => void,
  status: string,
  businessSuccess?: boolean,
  failReason?: string,
  contentSize?: string
};
function getUrlWithQuery (url: string, query?: ?{ [string]: string }) {
  const newQuery = Object.assign({}, query, { _t: +new Date() })
  return (
    url +
    (url.indexOf('?') === -1 ? '?' : '&') +
    querystring.stringify(newQuery)
  )
}
function sendAjaxTime (url: string, opt: prefOptoin) {
  try {
    const totalTime = opt.end()
    const urlSp = (url || '').split('?')
    if (urlSp.length === 0) return
    let param: string = ''
    if (urlSp.length > 1 && !opt.method) param = urlSp[1]
    opt.headers = opt.headers || {}
    if (opt.method === 'POST' && opt.headers['Content-Type']) {
      param = (opt.body || '').toString()
    } else if (typeof opt.body === 'object') {
      param = ((opt.body.get && opt.body.get('path')) || '').toString()
    }
    let option = {
      ajaxUrl: opt.path || urlSp[0],
      method: opt.method || 'GET',
      contentType: opt.headers['Content-Type'] || '',
      param,
      traceId: opt.traceId,
      businessSuccess: opt.businessSuccess,
      failReason: opt.failReason,
      contentSize: opt.contentSize
    }
    fengPerf.PerfClient.sendAjaxTime(option, totalTime, opt.status)
  } catch (e) {
    console.log('sendAjaxTime error', e)
  }
}

export default class Client {
  baseUrl: string;
  credentials: 'same-origin' | 'include';
  fetchOptsModifier: (opts: FetchOpts) => FetchOpts;

  constructor (
    baseUrl: string,
    fetchOptsModifier?: (opts: FetchOpts) => FetchOpts
  ) {
    this.baseUrl = baseUrl
    this.credentials = baseUrl.indexOf('/') === 0 ? 'same-origin' : 'include'
    this.fetchOptsModifier = fetchOptsModifier || (a => a)
  }

  fetchJson (url: string, opts: FetchOpts): Promise<*> {
    let perfOptions = { ...opts }
    perfOptions.traceId = fengPerf.PerfClient.getTraceID()
    perfOptions.end = fengPerf.Performance.getEnd()
    return fetch(url, this.fetchOptsModifier(opts))
      .then(result => {
        perfOptions.status = result.status.toString()
        perfOptions.contentSize = result.headers.get('Content-Length')
        if (result.status === 401) {
          sendAjaxTime(url, perfOptions)
          throw new KnownError({ message: 'Unauthorized', code: 401 })
        }
        return result.json()
      })
      .catch((e: Error) => {
        sendAjaxTime(url, perfOptions)
        if (e instanceof KnownError) {
          throw e
        }
        throw new KnownError({
          message: '网络异常',
          code: 0
        })
      })
      .then(result => {
        if (result.success == null) {
          // 旧接口
          if (result.error) {
            perfOptions.businessSuccess = false
            perfOptions.failReason = result.error
            sendAjaxTime(url, perfOptions)
            throw new KnownError({ message: result.error, code: -1 })
          }
          sendAjaxTime(url, perfOptions)
          return result
        }

        if (result.success) {
          sendAjaxTime(url, perfOptions)
          return result.data
        }

        const { code, message } = result.error || {}
        perfOptions.businessSuccess = false
        perfOptions.failReason = message
        sendAjaxTime(url, perfOptions)
        throw new KnownError({ message, code })
      })
  }

  get (path: string, query?: ?Query, headers?: Headers = {}): Promise<*> {
    const url = getUrlWithQuery(this.baseUrl + path, query)
    const opts = {
      headers,
      credentials: this.credentials,
      path: (query || {}).path
    }
    return this.fetchJson(url, opts)
  }

  postJson (path: string, body: mixed, headers?: Headers = {}): Promise<*> {
    const url = this.baseUrl + path
    headers['Content-Type'] = 'application/json'
    const opts = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: this.credentials,
      path: body && body.path && typeof body.path === 'string' ? body.path : ''
    }
    return this.fetchJson(url, opts)
  }

  encryptPost (path: string, body: mixed, headers?: Headers = {}): Promise<*> {
    return import('./encryptPost').then(module => {
      return module.default(body).then(body => {
        return this.postJson(path, body, headers)
      })
    })
  }

  postForm (
    path: string,
    body: { [string]: string },
    headers?: Headers = {}
  ): Promise<*> {
    const url = this.baseUrl + path
    headers['Content-Type'] =
      'application/x-www-form-urlencoded; charset=utf-8'
    const opts = {
      method: 'POST',
      headers,
      body: querystring.stringify(body),
      credentials: this.credentials,
      path: (body || {}).path
    }
    return this.fetchJson(url, opts)
  }

  postFormData (
    path: string,
    formData: { [string]: any },
    headers?: Headers = {}
  ): Promise<*> {
    const url = this.baseUrl + path
    let body = new window.FormData()
    for (let key in formData) {
      const value = formData[key]
      if (value != null) {
        if (value.blob) {
          body.append(key, value.blob, value.fileName)
        } else {
          body.append(key, value)
        }
      }
    }
    const opts = {
      method: 'POST',
      headers,
      body,
      credentials: this.credentials,
      path: (body.get && body.get('path')) || ''
    }
    return this.fetchJson(url, opts)
  }
}



// WEBPACK FOOTER //
// ./src/frame/net/HttpClient.js